import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Logo from "@/components/Logo";
import { SUPPORTED_LANGS } from "@/i18n";

const NAV = [
  { to: (l) => `/${l}#sectors`, key: "nav.sectors" },
  { to: (l) => `/${l}#analysis`, key: "nav.analysis" },
  { to: (l) => `/${l}#contact`, key: "nav.contact" },
];

export default function Header() {
  const { t } = useTranslation();
  const { lang: langParam } = useParams();
  const lang = SUPPORTED_LANGS.includes(langParam) ? langParam : "ar";
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  const switchLang = (target) => {
    if (target === lang) return;
    // Replace the first path segment if it's a lang; otherwise prepend.
    const parts = location.pathname.split("/").filter(Boolean);
    if (parts.length && SUPPORTED_LANGS.includes(parts[0])) parts[0] = target;
    else parts.unshift(target);
    const nextPath = "/" + parts.join("/") + location.hash;
    navigate(nextPath, { replace: false });
  };

  const goAnchor = (id, e) => {
    e?.preventDefault?.();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    else navigate(`/${lang}#${id}`);
    setOpen(false);
  };

  return (
    <header className={`ag-header ${scrolled ? "is-scrolled" : ""}`}>
      <div className="ag-container ag-header__inner">
        <NavLink to={`/${lang}`} className="ag-header__brand" aria-label="Argengeo">
          <Logo />
        </NavLink>

        <nav className="ag-header__nav" aria-label="primary">
          <NavLink end to={`/${lang}`} className="ag-nav-link">
            {t("nav.home")}
          </NavLink>
          {NAV.map((item) => {
            const id = item.to(lang).split("#")[1];
            return (
              <a
                key={item.key}
                href={item.to(lang)}
                onClick={(e) => goAnchor(id, e)}
                className="ag-nav-link"
              >
                {t(item.key)}
              </a>
            );
          })}
        </nav>

        <div className="ag-header__actions">
          <div className="ag-lang" role="group" aria-label={t("nav.language")}>
            <button
              type="button"
              onClick={() => switchLang("ar")}
              aria-pressed={lang === "ar"}
              className={`ag-lang__btn ${lang === "ar" ? "is-active" : ""}`}
            >
              {t("nav.languageES")}
            </button>
            <span className="ag-lang__sep" aria-hidden="true" />
            <button
              type="button"
              onClick={() => switchLang("en")}
              aria-pressed={lang === "en"}
              className={`ag-lang__btn ${lang === "en" ? "is-active" : ""}`}
            >
              {t("nav.languageEN")}
            </button>
          </div>

          <a
            href={`/${lang}#contact`}
            onClick={(e) => goAnchor("contact", e)}
            className="ag-btn ag-btn--primary ag-header__cta"
          >
            {t("nav.ctaContact")}
          </a>

          <button
            type="button"
            className={`ag-burger ${open ? "is-open" : ""}`}
            aria-expanded={open}
            aria-label={open ? t("nav.closeMenu") : t("nav.openMenu")}
            onClick={() => setOpen((v) => !v)}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </div>

      <div className={`ag-drawer ${open ? "is-open" : ""}`} aria-hidden={!open}>
        <nav className="ag-drawer__nav" aria-label="mobile">
          <NavLink end to={`/${lang}`} className="ag-drawer__link" onClick={() => setOpen(false)}>
            {t("nav.home")}
          </NavLink>
          {NAV.map((item) => {
            const id = item.to(lang).split("#")[1];
            return (
              <a
                key={item.key}
                href={item.to(lang)}
                onClick={(e) => goAnchor(id, e)}
                className="ag-drawer__link"
              >
                {t(item.key)}
              </a>
            );
          })}
          <NavLink to={`/${lang}/privacy-policy`} className="ag-drawer__link">
            {t("nav.privacy")}
          </NavLink>
          <NavLink to={`/${lang}/cookie-policy`} className="ag-drawer__link">
            {t("nav.cookies")}
          </NavLink>
          <NavLink to={`/${lang}/terms`} className="ag-drawer__link">
            {t("nav.terms")}
          </NavLink>
          <a
            href={`/${lang}#contact`}
            onClick={(e) => goAnchor("contact", e)}
            className="ag-btn ag-btn--primary ag-drawer__cta"
          >
            {t("nav.ctaContact")}
          </a>
        </nav>
      </div>
    </header>
  );
}
